import type { ChartBucket, ChartViewMode } from '@/utils/chartRangeNormalizer';
import { Tooltip } from 'chart.js';
import { formatChartLabel } from '@/utils/dt';

(Tooltip.positioners as unknown as Record<string, unknown>).barTop = (
    items: Array<{ element: { x: number; y: number } }>,
) => {
    if (!items.length) return false;
    const x = items.reduce((sum, item) => sum + item.element.x, 0) / items.length;
    return { x, y: Math.min(...items.map((item) => item.element.y)) };
};

const TOOLTIP_CLASS = 'bar-chart-tooltip';
const CARET_SIZE = 7;

const getTooltipEl = (canvas: HTMLCanvasElement) => {
    const parent = canvas.parentNode as HTMLElement;
    const found = parent.querySelector<HTMLDivElement>(`.${TOOLTIP_CLASS}`);
    if (found) return found;

    parent.style.position = 'relative';
    const el = document.createElement('div');
    el.className = TOOLTIP_CLASS;
    el.style.cssText = [
        'position:absolute', 'pointer-events:none', 'white-space:nowrap', 'z-index:1',
        'background:#FFFFFF', 'border:1px solid #E6E9EB', 'border-radius:10px',
        'box-shadow:8px 10px 50px rgba(0,0,0,0.05)', 'padding:12px',
        'font-family:Archivo,sans-serif',
        'opacity:0', 'transition:opacity 0.2s', 'transform:translate(-50%,-100%)',
    ].join(';');
    const title = document.createElement('div');
    title.style.cssText = 'font-size:13px;color:rgba(0,0,0,0.5);margin-bottom:6px';
    const body = document.createElement('div');
    body.style.cssText = 'font-size:14px;font-weight:600;color:#000000;white-space:pre;line-height:1.5';
    const caret = document.createElement('div');
    caret.style.cssText = 'position:absolute;bottom:0';
    const triangle = (color: string, top: number) => {
        const t = document.createElement('div');
        t.style.cssText = [
            'position:absolute', `left:-${CARET_SIZE}px`, `top:${top}px`, 'width:0', 'height:0',
            `border-left:${CARET_SIZE}px solid transparent`,
            `border-right:${CARET_SIZE}px solid transparent`,
            `border-top:${CARET_SIZE}px solid ${color}`,
        ].join(';');
        return t;
    };
    caret.append(triangle('#E6E9EB', 0), triangle('#FFFFFF', -1));
    el.append(title, body, caret);
    parent.appendChild(el);
    return el;
};

type ExternalTooltipArgs = {
    chart: { canvas: HTMLCanvasElement; width: number }
    tooltip: {
        opacity: number
        caretX: number
        caretY: number
        title: string[]
        body: Array<{ lines: string[] }>
    }
}

export const renderTooltip = ({ chart, tooltip }: ExternalTooltipArgs) => {
    const el = getTooltipEl(chart.canvas);
    if (!tooltip.opacity) {
        el.style.opacity = '0';
        return;
    }

    const [title = ''] = tooltip.title;
    const [titleEl, bodyEl, caretEl] = el.children as unknown as HTMLElement[];
    titleEl.textContent = title;
    bodyEl.textContent = tooltip.body.flatMap((entry) => entry.lines ?? []).join('\n');

    const half = el.offsetWidth / 2;
    const x = Math.min(Math.max(tooltip.caretX, half), chart.width - half);
    el.style.left = `${x}px`;
    el.style.top = `${tooltip.caretY - CARET_SIZE}px`;

    const inset = CARET_SIZE + 10; // clear of the 10px corner radius
    caretEl.style.left = `${Math.min(Math.max(tooltip.caretX - (x - half), inset), el.offsetWidth - inset)}px`;
    el.style.opacity = '1';
};

export const externalTooltip = (callbacks: Record<string, unknown>) => ({
    enabled: false,
    external: renderTooltip,
    position: 'barTop' as const,
    animation: { duration: 0 },
    callbacks,
});

export const bucketTitle = (
    label: string,
    viewMode: ChartViewMode,
    bucket: ChartBucket | null,
): string => (
    viewMode === 'weeks' && bucket
        ? `${formatChartLabel(bucket.from, 'weeks')} - ${formatChartLabel(bucket.to, 'weeks')}`
        : label
);
